import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ShoppingCart, MapPin, CreditCard, Package, CheckCircle, Store, Truck, Wallet, Shield, Zap, PiggyBank, Users } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";

const HowItWorks = () => {
  const { language } = useLanguage();

  // Contenu statique pour les acheteurs
  const buyerSteps = language === 'fr' ? [
    {
      step: 1,
      title: "Parcourez notre catalogue",
      description: "Explorez des milliers de produits de vendeurs locaux : électronique, mode, maison, beauté, alimentation et plus encore. Utilisez les filtres pour trouver exactement ce que vous cherchez.",
      icon: ShoppingCart
    },
    {
      step: 2,
      title: "Choisissez votre point relais",
      description: "Sélectionnez un point relais proche de chez vous parmi notre réseau. Les points relais sont des commerces partenaires où vous récupérerez vos colis en toute sécurité.",
      icon: MapPin
    },
    {
      step: 3,
      title: "Payez en toute sécurité",
      description: "Réglez par carte bancaire ou Mobile Money (M-Pesa, Airtel Money, Orange Money). Paiement 100% sécurisé via Stripe.",
      icon: CreditCard
    },
    {
      step: 4,
      title: "Récupérez votre colis",
      description: "Recevez une notification quand votre colis arrive. Présentez votre QR code au point relais pour récupérer votre commande. Vous avez 7 jours pour la retirer !",
      icon: Package
    }
  ] : [
    {
      step: 1,
      title: "Browse our catalog",
      description: "Explore thousands of products from local sellers: electronics, fashion, home, beauty, food and more. Use filters to find exactly what you're looking for.",
      icon: ShoppingCart
    },
    {
      step: 2,
      title: "Choose your relay point",
      description: "Select a relay point near you from our network. Relay points are partner stores where you will pick up your packages safely.",
      icon: MapPin
    },
    {
      step: 3,
      title: "Pay securely",
      description: "Pay by credit card or Mobile Money (M-Pesa, Airtel Money, Orange Money). 100% secure payment via Stripe.",
      icon: CreditCard
    },
    {
      step: 4,
      title: "Pick up your package",
      description: "Receive a notification when your package arrives. Present your QR code at the relay point to collect your order. You have 7 days to pick it up!",
      icon: Package
    }
  ];

  // Contenu statique pour les vendeurs
  const sellerSteps = language === 'fr' ? [
    {
      step: 1,
      title: "Inscrivez-vous gratuitement",
      description: "Créez votre compte vendeur en quelques minutes. Soumettez votre candidature avec vos informations d'entreprise et un document d'identité.",
      benefits: ["Inscription gratuite", "Validation sous 24-48h"],
      icon: Users
    },
    {
      step: 2,
      title: "Publiez vos produits",
      description: "Ajoutez vos articles avec photos, descriptions et prix. Gérez votre stock facilement depuis votre tableau de bord vendeur.",
      benefits: ["Photos multiples", "Catégories variées", "Gestion stock"],
      icon: Store
    },
    {
      step: 3,
      title: "Déposez au point relais",
      description: "Quand vous recevez une commande, déposez le colis au point relais choisi par l'acheteur sous 48 heures.",
      benefits: ["Délai 48h", "Réseau de points relais partenaires"],
      icon: Truck
    },
    {
      step: 4,
      title: "Recevez votre paiement",
      description: "Une fois le colis récupéré par l'acheteur, votre paiement est déclenché sous 7 jours. Commission de seulement 15%.",
      benefits: ["Paiement sécurisé", "85% pour vous", "Suivi temps réel"],
      icon: Wallet
    }
  ] : [
    {
      step: 1,
      title: "Sign up for free",
      description: "Create your seller account in minutes. Submit your application with your business information and ID document.",
      benefits: ["Free registration", "Validation within 24-48h"],
      icon: Users
    },
    {
      step: 2,
      title: "List your products",
      description: "Add your items with photos, descriptions and prices. Easily manage your inventory from your seller dashboard.",
      benefits: ["Multiple photos", "Various categories", "Stock management"],
      icon: Store
    },
    {
      step: 3,
      title: "Drop off at relay point",
      description: "When you receive an order, drop off the package at the relay point chosen by the buyer within 48 hours.",
      benefits: ["48h deadline", "Partner relay point network"],
      icon: Truck
    },
    {
      step: 4,
      title: "Get paid",
      description: "Once the package is picked up by the buyer, your payment is triggered within 7 days. Only 15% commission.",
      benefits: ["Secure payment", "85% for you", "Real-time tracking"],
      icon: Wallet
    }
  ];

  // Avantages Nazandoo
  const benefits = language === 'fr' ? [
    { icon: Shield, title: "Sécurité", description: "Paiement sécurisé Stripe, QR codes uniques, vérification d'identité vendeurs" },
    { icon: Zap, title: "Simplicité", description: "Interface intuitive, processus d'achat en 3 clics, tableau de bord clair" },
    { icon: PiggyBank, title: "Économie", description: "Pas de frais de livraison exorbitants, commission vendeur de seulement 15%" },
    { icon: MapPin, title: "Proximité", description: "Réseau de points relais dans toute la ville, récupérez à côté de chez vous" }
  ] : [
    { icon: Shield, title: "Security", description: "Stripe secure payment, unique QR codes, seller identity verification" },
    { icon: Zap, title: "Simplicity", description: "Intuitive interface, 3-click purchase process, clear dashboard" },
    { icon: PiggyBank, title: "Savings", description: "No exorbitant delivery fees, seller commission of only 15%" },
    { icon: MapPin, title: "Proximity", description: "Network of relay points throughout the city, pick up near you" }
  ];

  // Parcours commande
  const orderJourney = language === 'fr' ? [
    { phase: "Commande", steps: ["Acheteur ajoute au panier", "Sélectionne un point relais", "Paie en ligne (Carte/Mobile Money)", "Reçoit confirmation + QR code"] },
    { phase: "Préparation", steps: ["Vendeur reçoit notification", "Prépare le colis", "Dépose au point relais (48h max)", "Scanne le QR de dépôt"] },
    { phase: "Retrait", steps: ["Acheteur notifié de l'arrivée", "Se rend au point relais", "Présente son QR code", "Récupère son colis (7 jours)"] },
    { phase: "Paiement", steps: ["Retrait confirmé par QR scan", "Commission 15% prélevée", "Vendeur reçoit 85% sous 7 jours", "Tout le monde est satisfait !"] }
  ] : [
    { phase: "Order", steps: ["Buyer adds to cart", "Selects a relay point", "Pays online (Card/Mobile Money)", "Receives confirmation + QR code"] },
    { phase: "Preparation", steps: ["Seller receives notification", "Prepares the package", "Drops off at relay point (48h max)", "Scans the drop-off QR"] },
    { phase: "Pickup", steps: ["Buyer notified of arrival", "Goes to relay point", "Presents their QR code", "Picks up package (7 days)"] },
    { phase: "Payment", steps: ["Pickup confirmed by QR scan", "15% commission deducted", "Seller receives 85% within 7 days", "Everyone is satisfied!"] }
  ];

  // Statistiques
  const stats = language === 'fr' ? [
    { value: "15%", label: "Commission", description: "Une des plus basses du marché" },
    { value: "7 jours", label: "Délai paiement", description: "Après retrait acheteur" },
    { value: "48h", label: "Délai dépôt", description: "Pour les vendeurs" },
    { value: "3", label: "Modes de paiement", description: "Carte, M-Pesa, Orange" }
  ] : [
    { value: "15%", label: "Commission", description: "One of the lowest on the market" },
    { value: "7 days", label: "Payment delay", description: "After buyer pickup" },
    { value: "48h", label: "Drop-off delay", description: "For sellers" },
    { value: "3", label: "Payment methods", description: "Card, M-Pesa, Orange" }
  ];

  // FAQ
  const faqItems = language === 'fr' ? [
    { question: "Combien coûte l'inscription vendeur ?", answer: "L'inscription est 100% gratuite ! Nous prélevons uniquement une commission de 15% sur chaque vente réalisée." },
    { question: "Comment fonctionne le paiement vendeur ?", answer: "Dès que l'acheteur récupère son colis (confirmé par scan QR), nous déclenchons votre paiement sous 7 jours ouvrés. Vous recevez 85% du prix de vente." },
    { question: "Que se passe-t-il si l'acheteur ne récupère pas son colis ?", answer: "L'acheteur dispose de 7 jours pour récupérer son colis. Passé ce délai, le colis est retourné au vendeur et l'acheteur est remboursé (moins les frais éventuels)." },
    { question: "Puis-je vendre n'importe quel produit ?", answer: "Nous acceptons la plupart des produits légaux : électronique, mode, maison, beauté, alimentation non périssable, etc. Certains produits sont interdits (armes, contrefaçons, produits dangereux)." },
    { question: "Comment devenir point relais partenaire ?", answer: "Si vous possédez un commerce (boutique, restaurant, pharmacie...) et souhaitez devenir point relais, contactez-nous via la page Support. Vous gagnerez une commission sur chaque colis retiré." },
    { question: "Quels modes de paiement sont acceptés ?", answer: "Nous acceptons les cartes bancaires (Visa, Mastercard) et le Mobile Money (M-Pesa, Airtel Money, Orange Money). Tous les paiements sont sécurisés par Stripe." }
  ] : [
    { question: "How much does seller registration cost?", answer: "Registration is 100% free! We only charge a 15% commission on each sale made." },
    { question: "How does seller payment work?", answer: "As soon as the buyer picks up their package (confirmed by QR scan), we trigger your payment within 7 business days. You receive 85% of the sale price." },
    { question: "What happens if the buyer does not pick up their package?", answer: "The buyer has 7 days to pick up their package. After this period, the package is returned to the seller and the buyer is refunded (minus any fees)." },
    { question: "Can I sell any product?", answer: "We accept most legal products: electronics, fashion, home, beauty, non-perishable food, etc. Some products are prohibited (weapons, counterfeits, dangerous goods)." },
    { question: "How do I become a partner relay point?", answer: "If you own a business (store, restaurant, pharmacy...) and want to become a relay point, contact us via the Support page. You will earn a commission on each package picked up." },
    { question: "What payment methods are accepted?", answer: "We accept credit cards (Visa, Mastercard) and Mobile Money (M-Pesa, Airtel Money, Orange Money). All payments are secured by Stripe." }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container py-12 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-pink-500 bg-clip-text text-transparent">
            {language === 'fr' ? 'Comment ça marche ?' : 'How Does It Work?'}
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {language === 'fr' 
              ? <>Découvrez comment <strong className="text-foreground">Nazandoo</strong> révolutionne le commerce en ligne en RDC avec un système de <strong className="text-foreground">points relais</strong> pratique et sécurisé. Fini les livraisons manquées, fini les frais exorbitants !</>
              : <>Discover how <strong className="text-foreground">Nazandoo</strong> is revolutionizing online commerce in DRC with a <strong className="text-foreground">convenient and secure relay point</strong> system. No more missed deliveries, no more exorbitant fees!</>
            }
          </p>
        </div>

        {/* For Buyers Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
            🛒 {language === 'fr' ? 'Pour les Acheteurs' : 'For Buyers'}
          </h2>
          <div className="grid gap-6">
            {buyerSteps.map((item) => (
              <div key={item.step} className="flex gap-4 items-start bg-card p-6 rounded-xl border border-border hover:shadow-lg transition-shadow">
                <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <item.icon className="h-5 w-5 text-blue-500" />
                    <h3 className="text-xl font-semibold">{item.title}</h3>
                  </div>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* For Sellers Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
            🏪 {language === 'fr' ? 'Pour les Vendeurs' : 'For Sellers'}
          </h2>
          <div className="grid gap-6 max-w-4xl mx-auto">
            {sellerSteps.map((item) => (
              <div key={item.step} className="bg-card p-6 rounded-xl border border-border hover:shadow-xl transition-all">
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg">
                    {item.step}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <item.icon className="h-5 w-5 text-green-500" />
                      <h3 className="text-xl font-semibold">{item.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed mb-3">{item.description}</p>
                    <p className="text-sm font-medium text-primary">
                      {item.benefits.map((b, i) => (
                        <span key={i}>✓ {b}{i < item.benefits.length - 1 ? ' • ' : ''}</span>
                      ))}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-primary-foreground rounded-2xl p-8 md:p-12 shadow-2xl mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">
            💎 {language === 'fr' ? 'Pourquoi Choisir Nazandoo ?' : 'Why Choose Nazandoo?'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl mb-4 shadow-lg">
                  <benefit.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">{benefit.title}</h3>
                <p className="text-white/90 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Process Flow Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
            📦 {language === 'fr' ? "Le Parcours d'une Commande" : 'Order Journey'}
          </h2>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {orderJourney.map((phase, index) => (
              <div key={index} className="bg-card p-6 rounded-xl border border-border hover:shadow-lg transition-shadow">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full text-white font-bold text-lg shadow-lg">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-bold mt-3 text-foreground">{phase.phase}</h3>
                </div>
                <ul className="space-y-2">
                  {phase.steps.map((step, stepIndex) => (
                    <li key={stepIndex} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Stats Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
            📊 {language === 'fr' ? 'Nazandoo en Chiffres' : 'Nazandoo in Numbers'}
          </h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6 bg-card rounded-xl border border-border hover:shadow-lg transition-shadow">
                <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-lg font-semibold text-foreground mb-1">{stat.label}</div>
                <div className="text-sm text-muted-foreground">{stat.description}</div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
            ❓ {language === 'fr' ? 'Questions Fréquentes' : 'Frequently Asked Questions'}
          </h2>
          <div className="max-w-4xl mx-auto space-y-4">
            {faqItems.map((faq, index) => (
              <div key={index} className="bg-card p-6 rounded-xl border border-border">
                <h3 className="text-lg font-semibold mb-3 text-foreground">{faq.question}</h3>
                <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
            🚀 {language === 'fr' ? 'Prêt à Commencer ?' : 'Ready to Get Started?'}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {language === 'fr' 
              ? "Rejoignez des milliers d'utilisateurs qui font confiance à Nazandoo pour leurs achats et ventes en ligne."
              : "Join thousands of users who trust Nazandoo for their online buying and selling."
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/products"
              className="px-8 py-4 rounded-xl font-semibold text-lg transition-all transform hover:scale-105 shadow-lg bg-gradient-to-r from-primary to-pink-500 text-primary-foreground hover:shadow-xl"
            >
              {language === 'fr' ? 'Découvrir les produits' : 'Discover products'}
            </a>
            <a
              href="/become-vendor"
              className="px-8 py-4 rounded-xl font-semibold text-lg transition-all transform hover:scale-105 shadow-lg bg-card text-foreground border border-border hover:shadow-xl"
            >
              {language === 'fr' ? 'Devenir vendeur' : 'Become a seller'}
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HowItWorks;
